import React, { useState, useEffect } from 'react'
import { GetStaticPaths, GetStaticProps } from 'next'
import getEMTStaticProps from '@/helpers/getEMTStaticProps'
import { EMTStaticProps } from '@/helpers/getEMTStaticProps'
import { SEO, withPublicRoute } from '@/components'
import { Routes } from '@/assets/configs'
import { useRouter } from 'next/router'
import { Navigation } from '@/components/EMT/Navigation'
import { useMobile, EmtTypography } from 'emt-components-v2'
import Box from '@mui/material/Box'
import PublicPetSitterProfileComponent from '@/components/Profile/PublicPetSitterProfileComponent'
import PetSittingNav from '@/components/PetSitter/PetSittingNav'
import PublicNavigation from '@/components/EMT/PublicNavigation'
import PublicPetSittingNav from '@/components/PetSitter/PublicPetSittingNav'
import { useIsQueryOwner } from '@/hooks'
import useCurrentUser from '@/hooks/useCurrentUser'
import NavNextLink from '@/components/EMT/NavNextLink'
import { unlockScroll } from '@/utils/scrollLock'
import { usePetsittingMode } from '@/store/usePetsittingMode'

import { getProfileAsync } from '@/modules/PetSitter/viewProfileServiceHelper'
import { UrlParamType } from '@/modules/utils'

type Props = EMTStaticProps & {
  customerId: string
  seo: {
    firstName: string | null
    city: string | null
  }
  profile: any
}

export function buildPetSitterSeoSubtitle(params: { city?: string | null; firstName?: string | null }) {
  const cityPart = params.city ? `à ${params.city}` : ''
  const namePart = params.firstName ? `– ${params.firstName}` : ''
  // Example: "Pet sitter à Toulouse – Marie"
  return `Pet sitter ${cityPart} ${namePart}`.replace(/\s+/g, ' ').trim()
}

const ViewProfilePage: React.FC<Props> = ({ url, title, customerId, seo, profile }) => {
  const [isMobile] = useMobile()
  const [, type] = useIsQueryOwner()
  const [userProfileName, setUserProfileName] = useState<string>()
  const router = useRouter()
  const { currentUser } = useCurrentUser()
  const { isPetsittingMode } = usePetsittingMode()

  const [isNavigating, setIsNavigating] = useState(false)

  useEffect(() => {
    const handleStart = () => setIsNavigating(true)
    const handleEnd = () => setIsNavigating(false)

    router.events.on('routeChangeStart', handleStart)
    router.events.on('routeChangeComplete', handleEnd)
    router.events.on('routeChangeError', handleEnd)

    return () => {
      router.events.off('routeChangeStart', handleStart)
      router.events.off('routeChangeComplete', handleEnd)
      router.events.off('routeChangeError', handleEnd)
    }
  }, [])

  useEffect(() => {
    unlockScroll()
    return () => unlockScroll()
  }, [])

  useEffect(() => {
    if (type == 'pro' && currentUser) {
      router.push(Routes.Pro.Dashboard)
    }
    if (type === 'borrower' && currentUser) {
      router.push(Routes.Home)
    }
  }, [currentUser, type])

  // Redirect to profile if currentUser is present on mount
  useEffect(() => {
    if (!router.isReady) return

    if (currentUser && !isNavigating) {
      router.replace(Routes.ProfilePetSitter(customerId))
    } else if (currentUser && isNavigating) {
      router.push({
        pathname: Routes.ProfilePetSitter(customerId),
        query: { registration: true, type: UrlParamType.Owner },
      })
    }
  }, [currentUser, router.isReady, isNavigating])

  // If no currentUser, set 'type' query param to 'dogowner' in the URL
  useEffect(() => {
    if (!currentUser && router.isReady) {
      const { type, ...restQuery } = router.query
      if (type !== 'dogowner') {
        router.replace(
          {
            pathname: router.pathname,
            query: { ...restQuery, pbview: 'true', type: 'dogowner' },
          },
          undefined,
          { shallow: true },
        )
      }
    }
  }, [currentUser, router])

  return (
    <>
      <SEO url={`${url}`} subtitle={seo.firstName || title} />
      {!isMobile && (
        <Box sx={{ overflow: 'hidden' }}>{currentUser ? <Navigation /> : <PublicNavigation petsitter />}</Box>
      )}
      {currentUser && !isMobile && !isPetsittingMode ? (
        <PetSittingNav />
      ) : (
        // <></>
        !isMobile && (
          <PublicPetSittingNav
            link={Routes.PetSitter.Search}
            title={
              <EmtTypography
                variant="body2"
                fontWeight="600"
                color="#fff"
                sx={{ display: 'flex', alignItems: 'center', gap: '15px' }}
              >
                <NavNextLink sx={{ color: '#fff' }} href={Routes.PetSitter.Search}>
                  Recherche Pet Sitter
                </NavNextLink>{' '}
                <span style={{ color: '#762D8A' }}>/</span>{' '}
                <span style={{ color: '#762D8A', fontWeight: '700' }}>
                  {userProfileName ? userProfileName : seo.firstName}
                </span>
              </EmtTypography>
            }
          />
        )
      )}
      {/* <h1>{profile.name}</h1> */}
      <PublicPetSitterProfileComponent
        customerId={profile?.uuid}
        profile={profile}
        getUserProfileName={(name: string) => setUserProfileName(name)}
        overrideBackButton={() => {
          const conversation = router.query.conversation
          if (conversation) {
            router.push({
              pathname: Routes.PetSitter.Reservations.Base,
              query: { conversation, type },
            })
          } else {
            if (window.history.length > 1) {
              router.back()
            } else {
              router.push(Routes.PetSitter.Search)
            }
          }
        }}
      />
    </>
  )
}

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [], // don’t prebuild anything at deploy
    fallback: 'blocking', // generate on first request
  }
}

export const getStaticProps: GetStaticProps = async (ctx) => {
  const customerId = ctx.params?.['customer-id'] as string

  const baseProps = await getEMTStaticProps({
    namespaces: ['common', 'profile', 'message', 'petsitter', 'connection'],
    i18nTitleNs: 'profile',
    i18nTitleKey: 'title',
  })(ctx)

  if ('notFound' in baseProps) return baseProps

  const publicProfile = (await getProfileAsync(customerId, true)) as any

  const seo = {
    firstName: publicProfile?.firstName ?? publicProfile?.displayName ?? publicProfile?.name ?? null,
    city: publicProfile?.city ?? null,
  }

  return {
    props: {
      ...('props' in baseProps ? baseProps.props : {}),
      customerId,
      seo,
      profile: publicProfile,
    },
    revalidate: 60 * 60,
  }
}

export default withPublicRoute()(ViewProfilePage)
